import { getTranslations, t, WidgetTranslations } from './translations';

export interface BusinessHoursDisplay {
  timezone: string;
  schedule: Array<{
    day_of_week: number;
    open_time: string | null;
    close_time: string | null;
    is_closed: boolean;
  }>;
  next_opening: string | null; // UTC ISO8601
}

// Display order: Monday first, Sunday last. Values are 0-6 (0=Sunday).
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Reference Sunday (2024-01-07) + offset to get a date whose weekday == dow.
function weekdayName(dow: number, locale: string, style: 'long' | 'short'): string {
  const d = new Date(Date.UTC(2024, 0, 7 + dow)); // 2024-01-07 is a Sunday (dow 0)
  return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(d);
}

function dayParts(iso: string, locale: string, tz: string): { day: string; time: string } {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: tz }).format(d);
  const time = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  }).format(d);
  return { day, time };
}

// Local weekday (0-6) for "today" in the given timezone.
function todayDow(tz: string, now: Date): number {
  const name = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz }).format(now);
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[name] ?? -1;
}

interface Row {
  days: number[];
  label: string; // time range or closed
  closed: boolean;
}

function buildRows(
  data: BusinessHoursDisplay,
  closedLabel: string,
): Row[] {
  const byDay = new Map<number, { open: string | null; close: string | null; closed: boolean }>();
  for (const s of data.schedule) {
    byDay.set(s.day_of_week, { open: s.open_time, close: s.close_time, closed: s.is_closed });
  }

  const rows: Row[] = [];
  for (const dow of DISPLAY_ORDER) {
    const e = byDay.get(dow);
    const closed = !e || e.closed || !e.open || !e.close;
    const label = closed ? closedLabel : `${e!.open}–${e!.close}`; // en dash
    const last = rows[rows.length - 1];
    if (last && last.label === label && last.closed === closed) {
      last.days.push(dow);
    } else {
      rows.push({ days: [dow], label, closed });
    }
  }
  return rows;
}

function rowDayLabel(days: number[], locale: string): string {
  if (days.length === 1) return weekdayName(days[0], locale, 'short');
  return `${weekdayName(days[0], locale, 'short')}–${weekdayName(days[days.length - 1], locale, 'short')}`;
}

/**
 * Render the business-hours block for the offline form.
 * Returns '' when there is nothing to show.
 */
export function renderBusinessHours(
  data: BusinessHoursDisplay | null | undefined,
  locale: string,
  visitorTimeZone: string,
  now: Date = new Date(),
  translations?: WidgetTranslations,
): string {
  if (!data || !data.schedule || data.schedule.length === 0) return '';

  const tr = translations || getTranslations(locale);
  const siteTz = data.timezone || 'UTC';
  const tzDiffers = !!visitorTimeZone && visitorTimeZone !== siteTz;

  // Summary line
  let mainText: string;
  let localText = '';
  if (data.next_opening) {
    const site = dayParts(data.next_opening, locale, siteTz);
    mainText = t(tr.bh_next_opening || '', { day: site.day, time: site.time });
    if (tzDiffers) {
      const local = dayParts(data.next_opening, locale, visitorTimeZone);
      localText = t(tr.bh_next_opening_local || '', { day: local.day, time: local.time });
    }
  } else {
    mainText = tr.bh_hours_label || 'Business hours';
  }

  // Schedule rows
  const closedLabel = tr.bh_closed || 'Closed';
  const rows = buildRows(data, closedLabel);
  const today = todayDow(siteTz, now);

  const rowsHtml = rows
    .map((r) => {
      const isToday = r.days.includes(today);
      const cls = ['quantumchat-bh-row', isToday ? 'is-today' : '', r.closed ? 'is-closed' : '']
        .filter(Boolean)
        .join(' ');
      return (
        `<li class="${cls}">` +
        `<span class="quantumchat-bh-day">${escapeHtml(rowDayLabel(r.days, locale))}</span>` +
        `<span class="quantumchat-bh-time">${escapeHtml(r.label)}</span>` +
        `</li>`
      );
    })
    .join('');

  const tzNote = tzDiffers
    ? `<p class="quantumchat-bh-tz">${escapeHtml(
        t(tr.bh_timezone_note || '', { site_tz: siteTz, local_tz: visitorTimeZone }),
      )}</p>`
    : '';

  const ariaLabel = escapeHtml(tr.bh_hours_label || 'Business hours');

  return (
    `<div class="quantumchat-businesshours" data-state="collapsed">` +
    `<button type="button" class="quantumchat-bh-summary" aria-expanded="false" aria-controls="quantumchat-bh-panel">` +
    `<svg class="quantumchat-bh-clock" viewBox="0 0 24 24" fill="none" aria-hidden="true">` +
    `<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>` +
    `<path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` +
    `</svg>` +
    `<span class="quantumchat-bh-summary-texts">` +
    `<span class="quantumchat-bh-summary-main">${escapeHtml(mainText)}</span>` +
    `<span class="quantumchat-bh-summary-local">${escapeHtml(localText)}</span>` +
    `</span>` +
    `<svg class="quantumchat-bh-chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">` +
    `<path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>` +
    `</svg>` +
    `</button>` +
    `<div class="quantumchat-bh-panel" id="quantumchat-bh-panel" role="region" aria-label="${ariaLabel}">` +
    `<div class="quantumchat-bh-panel-inner">` +
    `<ul class="quantumchat-bh-schedule">${rowsHtml}</ul>` +
    tzNote +
    `</div>` +
    `</div>` +
    `</div>`
  );
}
